import React, { useEffect, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { StatusDropdownProps } from './StatusDropdown.types';
import { AttioQueryKeys } from '../../../common/types/commonTypes';

const StatusDropdown = ({
  currentStatus,
  onStatusChange,
  isEditing,
  onEditEnd,
  statusOptions,
  loading = false,
}: StatusDropdownProps) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const queryClient = useQueryClient();

  // Check if status is final and should be read-only
  const isFinalStatus = currentStatus === 'Final Sent';

  useEffect(() => {
    setSelectedStatus(currentStatus || '');
  }, [currentStatus]);

  // Automatically open the dropdown when entering edit mode (only if not final status)
  useEffect(() => {
    if (isEditing && !isFinalStatus) {
      setDropdownOpen(true);
    }
  }, [isEditing, isFinalStatus]);

  const handleStatusChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
    // Prevent changes if status is final
    if (isFinalStatus) {
      return;
    }

    const newStatusTitle = event.target.value as string;
    setSelectedStatus(newStatusTitle);

    // Find the option ID for the selected status
    const selectedOption = statusOptions.find(option => option.title === newStatusTitle);

    if (onStatusChange && selectedOption) {
      onStatusChange(newStatusTitle, selectedOption.id.option_id);
    }

    // Refetch to get fresh data immediately
    await queryClient.refetchQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL],
      type: 'all',
    });

    // Close the dropdown after selecting
    setDropdownOpen(false);
    onEditEnd();
  };

  const handleStatusClose = () => {
    setDropdownOpen(false);
    onEditEnd();
    setSelectedStatus(currentStatus || '');
  };

  const handleStatusOpen = () => {
    setDropdownOpen(true);
  };

  // Don't render dropdown if not editing or if status is final
  if (!isEditing || isFinalStatus) {
    return null;
  }

  return (
    <TextField
      value={selectedStatus}
      select
      fullWidth
      variant="standard"
      onChange={handleStatusChange}
      onClick={e => {
        e.stopPropagation();
      }}
      autoFocus
      size="small"
      disabled={loading}
      SelectProps={{
        open: dropdownOpen,
        onClose: handleStatusClose,
        onOpen: handleStatusOpen,
        MenuProps: {
          autoFocus: false,
        },
      }}>
      <MenuItem value="" disabled>
        {loading ? 'Loading...' : 'Select Status'}
      </MenuItem>
      {statusOptions.map(option => (
        <MenuItem key={option.id.option_id} value={option.title}>
          {option.title}
        </MenuItem>
      ))}
    </TextField>
  );
};

export { StatusDropdown };
